import { useEffect, useRef } from 'react'
import DOMPurify from 'dompurify'

/** Render email HTML safely inside a shadow DOM host.
 *  - DOMPurify strips scripts, event handlers, and dangerous tags/attributes.
 *  - Shadow DOM isolates email CSS so it cannot leak into the dashboard.
 *  - Compared to an iframe: no height measurement, native text selection,
 *    proper accessibility, no painting/resize jank on mobile.
 */
export function SanitizedEmailHtml({
  html,
  inlineImages,
  onImageClick,
}: {
  html: string
  inlineImages?: Record<string, string>
  /** Opens an embedded image in the app's viewer; without it the image links to a new tab. */
  onImageClick?: (contentId: string) => void
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const onImageClickRef = useRef(onImageClick)
  useEffect(() => {
    onImageClickRef.current = onImageClick
  })
  const imagesOpenInViewer = !!onImageClick

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
    const sanitized = DOMPurify.sanitize(html, {
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'style'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
      ALLOW_DATA_ATTR: false,
    })

    shadow.innerHTML = `<style>
      :host { display: block; max-width: 100%; min-width: 0; }
      .root { font-family: system-ui, sans-serif; font-size: 14px; line-height: 1.5; color: #1a1a1a; overflow-wrap: break-word; overflow-x: auto; }
      .root * { max-width: 100%; min-width: 0 !important; box-sizing: border-box; }
      .root [style*="nowrap" i], .root [nowrap] { white-space: normal !important; }
      .root img, .root video { max-width: 100% !important; height: auto !important; min-height: 0 !important; }
      .root button.cid-image { display: inline-block; padding: 0; border: 0; background: none; cursor: zoom-in; border-radius: 4px; }
      .root button.cid-image:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
      .root table { max-width: 100% !important; width: auto !important; table-layout: auto; border-collapse: collapse; }
      .root td, .root th { overflow-wrap: break-word; }
      .root pre, .root code { white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }
      .root blockquote { margin-left: 0.5rem; padding-left: 0.5rem; border-left: 2px solid #e5e7eb; }
      .root a { color: #2563eb; overflow-wrap: anywhere; word-break: break-word; }
      .root p { margin: 0 0 0.5em; }
    </style><div class="root">${sanitized}</div>`
    // Signatures glue whole lines with non-breaking spaces; let a single one between words wrap.
    const walker = document.createTreeWalker(shadow, NodeFilter.SHOW_TEXT)
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.nodeValue ?? ''
      if (text.includes('\u00a0') && !node.parentElement?.closest('pre, code')) {
        node.nodeValue = text.replace(/(?<=\S)\u00a0(?=\S)/g, ' ')
      }
    }
    if (inlineImages) {
      for (const image of shadow.querySelectorAll('img')) {
        const source = image.getAttribute('src') ?? ''
        if (!source.toLowerCase().startsWith('cid:')) continue
        const url = inlineImages[source.slice(4)]
        if (typeof url !== 'string') {
          image.removeAttribute('src')
          continue
        }
        // Only app-created blob URLs are assigned after sanitizing the email.
        if (!url.startsWith('blob:')) continue
        image.src = url
        image.removeAttribute('srcset')
        // Linked logos/thumbnails keep the sender's original destination.
        if (image.closest('a')) continue
        if (imagesOpenInViewer) {
          const contentId = source.slice(4)
          const button = document.createElement('button')
          button.type = 'button'
          button.className = 'cid-image'
          button.setAttribute('aria-label', `View ${image.alt || 'image'}`)
          button.addEventListener('click', () => onImageClickRef.current?.(contentId))
          image.replaceWith(button)
          button.append(image)
          continue
        }
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        link.setAttribute('aria-label', `Open ${image.alt || 'image'}`)
        image.replaceWith(link)
        link.append(image)
      }
    }
  }, [html, inlineImages, imagesOpenInViewer])

  return <div ref={hostRef} className="w-full min-w-0" />
}
